/**
 * Custom QuadBubbleChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { QuadBubbleChartComponent as ParentQuadBubbleChartComponent } from '@shared-lib/chart-components/quad-bubble-chart/quad-bubble-chart.component';
import { CapitalCasePipe } from '@shared-lib/pipes/capital-case.pipe';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { executeNavigationFromConfig } from '@shared-lib/shared-services/chart-wrapper.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

@Component({
  selector: 'app-quad-bubble-chart',
  standalone: true,
  imports: [CommonModule, CapitalCasePipe],
  templateUrl: './quad-bubble-chart.component.html',
  styleUrls: ['./quad-bubble-chart.component.scss']
})
export class QuadBubbleChartComponent extends ParentQuadBubbleChartComponent {
  // Constructor is inherited from parent to avoid dependency injection issues
  private basePlotGeometry?: {
    xAxis: any;
    yAxis: any;
    quadrantBackgrounds: any[];
  };
  private bubbleColorBySeed = new Map<string, string>();
  private customLastBubbleInteractionAt = 0;
  private applicationByRowKey = new Map<string, any>();
  private customChartFilterHandler = inject(ChartFilterHandlerService);
  private customGlobalFilterService = inject(GlobalFilterService);
  private customRouter = inject(Router);

  get shouldUseSquarePlot(): boolean {
    return this.data?.squarePlotArea !== false && this.data?.squareMap !== false;
  }

  get squarePlotMaxWidth(): string {
    return String(
      this.data?.squarePlotMaxWidth ??
      this.data?.plotMaxWidth ??
      '44rem',
    ).trim();
  }

  protected override loadAndRenderChart(filters?: any): any {
    // TODO: Add your custom logic here
    return super.loadAndRenderChart(filters);
  }

  protected override renderChart(chartData?: any): any {
    let nextData = this.applyPlotPadding(this.data);

    if (this.shouldUseSquarePlot) {
      nextData = {
        ...nextData,
        chartConfig: {
          ...(nextData?.chartConfig || {}),
          grid: this.resolveSquareGrid(nextData?.chartConfig?.grid),
        },
      };
    }

    this.data = nextData;
    this.bubbleColorBySeed.clear();
    const result = super.renderChart(chartData);
    this.chart?.getDom()?.setAttribute('data-dvm-lock-visual-scale', 'true');
    this.applyQuadrantColorsToRenderedSeries();
    requestAnimationFrame(() => this.chart?.resize());
    this.applySafeTooltipPositioning();
    this.syncLegendColorsToBubbleColors();
    return result;
  }

  private applyQuadrantColorsToRenderedSeries(): void {
    if (!this.chart || this.data?.bubbleColorMode !== 'quadrant') {
      return;
    }

    const option = this.chart.getOption() as any;
    const series = Array.isArray(option?.series) ? option.series : [];
    if (!series.length) {
      return;
    }

    const patchedSeries = series.map((currentSeries: any) => {
      const points = Array.isArray(currentSeries?.data) ? currentSeries.data : [];
      if (!points.length) {
        return currentSeries;
      }

      return {
        ...currentSeries,
        data: points.map((point: any) => {
          const color = this.resolveQuadrantBubbleColor(point);
          return color
            ? {
              ...point,
              color,
            }
            : point;
        }),
      };
    });

    this.chart.setOption({ series: patchedSeries }, false);
  }

  private applySafeTooltipPositioning(): void {
    if (!this.chart) {
      return;
    }

    const chartDom = this.chart.getDom();
    if (!chartDom) {
      return;
    }

    this.chart.setOption({
      tooltip: {
        confine: true,
        position: (point: number[], _params: any, _dom: HTMLElement, _rect: any, size: any) => {
          const gap = 12;
          const edgePadding = 8;
          const viewSize = Array.isArray(size?.viewSize) ? size.viewSize : [chartDom.clientWidth, chartDom.clientHeight];
          const contentSize = Array.isArray(size?.contentSize) ? size.contentSize : [280, 120];
          const tooltipWidth = Math.min(contentSize[0] || 280, Math.max(160, (viewSize?.[0] || chartDom.clientWidth) - (edgePadding * 2)));
          const tooltipHeight = contentSize[1] || 120;
          const viewWidth = viewSize?.[0] || chartDom.clientWidth || 0;
          const viewHeight = viewSize?.[1] || chartDom.clientHeight || 0;

          const leftX = point[0] - tooltipWidth - gap;
          const rightX = point[0] + gap;
          const preferredAboveY = point[1] - tooltipHeight - gap;
          const preferredBelowY = point[1] + gap;

          let x = leftX;
          if (leftX < edgePadding) {
            x = rightX;
          }
          if (x + tooltipWidth > viewWidth - edgePadding) {
            x = Math.max(edgePadding, viewWidth - tooltipWidth - edgePadding);
          }

          let y = preferredAboveY;
          if (preferredAboveY < edgePadding) {
            y = preferredBelowY;
          }
          if (y + tooltipHeight > viewHeight - edgePadding) {
            y = Math.max(edgePadding, viewHeight - tooltipHeight - edgePadding);
          }
          if (y <= point[1] && y + tooltipHeight >= point[1]) {
            const belowCandidate = preferredBelowY;
            const aboveCandidate = preferredAboveY;
            if (belowCandidate + tooltipHeight <= viewHeight - edgePadding) {
              y = belowCandidate;
            } else if (aboveCandidate >= edgePadding) {
              y = aboveCandidate;
            }
          }

          return [Math.round(x), Math.round(y)];
        },
      },
    }, false);
  }

  private applyPlotPadding(data: any): any {
    const topPadding = this.resolvePlotPadding('top');
    if (topPadding <= 0) {
      return data;
    }

    const baseGeometry = this.getBasePlotGeometry(data);
    const yAxis = { ...(baseGeometry.yAxis || {}) };
    const yMax = Number(yAxis.max ?? 10);
    if (!Number.isFinite(yMax)) {
      return data;
    }

    const paddedYMax = yMax + topPadding;
    const quadrantBackgrounds = baseGeometry.quadrantBackgrounds.map((quad: any) =>
      this.expandTopQuadrantBoundary(quad, yMax, paddedYMax),
    );

    return {
      ...data,
      yAxis: {
        ...yAxis,
        max: paddedYMax,
      },
      quadrantBackgrounds,
    };
  }

  private getBasePlotGeometry(data: any): { xAxis: any; yAxis: any; quadrantBackgrounds: any[] } {
    if (!this.basePlotGeometry) {
      this.basePlotGeometry = {
        xAxis: { ...(data?.xAxis || {}) },
        yAxis: { ...(data?.yAxis || {}) },
        quadrantBackgrounds: Array.isArray(data?.quadrantBackgrounds)
          ? data.quadrantBackgrounds.map((quad: any) => ({ ...quad }))
          : [],
      };
    }

    return this.basePlotGeometry;
  }

  private expandTopQuadrantBoundary(quad: any, currentMax: number, paddedMax: number): any {
    const nextQuad = { ...quad };
    const yAxis = Number(nextQuad.yAxis);
    const y2 = Number(nextQuad.y2);

    if (Number.isFinite(yAxis) && yAxis === currentMax) {
      nextQuad.yAxis = paddedMax;
    }

    if (Number.isFinite(y2) && y2 === currentMax) {
      nextQuad.y2 = paddedMax;
    }

    return nextQuad;
  }

  private resolvePlotPadding(edge: 'top'): number {
    const configured = this.data?.plotPadding?.[edge] ?? this.data?.yAxisPaddingTop ?? this.data?.axisPaddingTop;
    return Math.max(0, this.resolveNumber(configured, 0));
  }

  private resolveSquareGrid(grid: any): any {
    const currentGrid = grid || {};
    const top = this.parsePercent(currentGrid.top);
    const bottom = this.parsePercent(currentGrid.bottom);
    const verticalMargin = top !== null && bottom !== null ? top + bottom : 19;
    const horizontalMarginOffset = this.resolveNumber(this.data?.squarePlotHorizontalMarginOffset, 8);
    const horizontalMargin = Math.max(0, verticalMargin + horizontalMarginOffset);
    const leftRatio = 0.62;
    const left = Math.round(horizontalMargin * leftRatio * 10) / 10;
    const right = Math.round((horizontalMargin - left) * 10) / 10;

    return {
      ...currentGrid,
      left: `${left}%`,
      right: `${right}%`,
      top: currentGrid.top ?? '8%',
      bottom: currentGrid.bottom ?? '11%',
      containLabel: currentGrid.containLabel ?? true,
    };
  }

  private parsePercent(value: any): number | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed.endsWith('%')) {
      return null;
    }

    const parsed = Number(trimmed.slice(0, -1));
    return Number.isFinite(parsed) ? parsed : null;
  }

  private resolveNumber(value: any, fallback: number): number {
    const resolved = Number(value);
    return Number.isFinite(resolved) ? resolved : fallback;
  }

  private syncLegendColorsToBubbleColors(): void {
    if (
      !this.chart
      || this.data?.syncLegendColorToBubble === false
      || this.data?.bubbleColorMode === 'quadrant'
    ) {
      return;
    }

    const option = this.chart.getOption() as any;
    const series = Array.isArray(option?.series) ? option.series : [];
    const patchedSeries = series.map((currentSeries: any) => {
      const points = Array.isArray(currentSeries?.data) ? currentSeries.data : [];
      const color = points.find((point: any) => point?.color)?.color || currentSeries?.itemStyle?.color;

      if (!color || typeof color === 'function') {
        return currentSeries;
      }

      return {
        name: currentSeries.name,
        itemStyle: {
          ...(currentSeries.itemStyle || {}),
          color,
        },
      };
    });

    if (patchedSeries.length) {
      this.chart.setOption({ series: patchedSeries }, false);
    }
  }

  protected override sanitizeUiOnlyFilters(filters?: any): any {
    // TODO: Add your custom logic here
    return super.sanitizeUiOnlyFilters(filters);
  }

  protected override formatCoordinateTooltipValue(value: any): string {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return '-';
    }

    return numericValue.toFixed(1);
  }

  protected override updateQuadrantTitleGraphics(): any {
    if (!this.chart) {
      return;
    }
    this.chart.setOption({ graphic: [] }, false);
  }

  protected override buildQuadrantPoints(points: any, quadrantIndex: any): any {
    const normalizedPoints = super.buildQuadrantPoints(points, quadrantIndex);
    return this.spreadOverlappingPoints(normalizedPoints);
  }

  private spreadOverlappingPoints(points: any[]): any[] {
    const offset = this.resolveNumber(
      this.data?.bubbleOverlapOffset ?? this.data?.overlapOffset,
      0,
    );

    if (!Array.isArray(points) || points.length < 2 || offset <= 0) {
      return points;
    }

    const grouped = new Map<string, any[]>();
    points.forEach((point) => {
      const x = Number(point?.x ?? point?.value?.[0]);
      const y = Number(point?.y ?? point?.value?.[1]);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return;
      }

      const key = `${x.toFixed(6)}|${y.toFixed(6)}`;
      const current = grouped.get(key) || [];
      current.push(point);
      grouped.set(key, current);
    });

    for (const overlappingPoints of grouped.values()) {
      if (overlappingPoints.length < 2) {
        continue;
      }

      overlappingPoints.forEach((point, index) => {
        const baseX = Number(point?.x ?? point?.value?.[0]);
        const baseY = Number(point?.y ?? point?.value?.[1]);
        const spreadIndex = index - ((overlappingPoints.length - 1) / 2);
        const nextX = this.clampToAxis(baseX + (spreadIndex * offset), 'x');
        const nextY = this.clampToAxis(baseY, 'y');

        point.x = nextX;
        point.y = nextY;
        point.value = [nextX, nextY, point?.quadrantIndex, point?.size];
      });
    }

    return points;
  }

  private clampToAxis(value: number, axis: 'x' | 'y'): number {
    const axisConfig = axis === 'x' ? this.data?.xAxis : this.data?.yAxis;
    const min = Number(axisConfig?.min ?? 0);
    const max = Number(axisConfig?.max ?? 10);

    if (!Number.isFinite(value)) {
      return value;
    }

    if (Number.isFinite(min) && value < min) {
      return min;
    }

    if (Number.isFinite(max) && value > max) {
      return max;
    }

    return value;
  }

  protected override buildLegendSeries(points: any): any {
    if (this.data?.hideLegend === true && this.data?.bubbleColorMode === 'quadrant') {
      const normalizedPoints = (Array.isArray(points) ? points : []).map((point: any) => {
        const color = this.resolveQuadrantBubbleColor(point);
        return color
          ? {
            ...point,
            color,
          }
          : point;
      });

      return normalizedPoints.length
        ? [{
          name: 'Quadrant',
          points: normalizedPoints,
          color: String(normalizedPoints[0]?.color || '#37A2DA'),
        }]
        : [];
    }

    return super.buildLegendSeries(points);
  }

  protected override buildBubbleColor(point: any, index: any, total: any): any {
    if (this.data?.bubbleColorMode === 'quadrant') {
      const quadrantColor = this.resolveQuadrantBubbleColor(point);
      if (quadrantColor) {
        return quadrantColor;
      }
    }

    const colorKey = String(
      this.data?.bubbleColorKey ??
      this.data?.colorKey ??
      this.data?.legendColorKey ??
      '',
    ).trim();
    const colorValue = colorKey ? this.resolveTooltipValue(point, colorKey) : undefined;
    const stableSeed = String(colorValue ?? '').trim();

    if (stableSeed) {
      return this.resolveStableColor(stableSeed);
    }

    return super.buildBubbleColor(point, index, total);
  }

  private resolveQuadrantBubbleColor(point: any): string | null {
    const quadrants = Array.isArray(this.data?.quadrantBackgrounds) ? this.data.quadrantBackgrounds : [];
    const x = Number(point?.x ?? point?.value?.[0]);
    const y = Number(point?.y ?? point?.value?.[1]);
    const preferProductPriceType = this.data?.bubbleColorPreferProductPriceType === true;
    const forceSelectedProductPriceType = this.data?.bubbleColorUseSelectedProductPriceType === true;
    const focusedQuadrant = this.getFocusedQuadrantColorSource(quadrants);
    const quadrantByProductPriceType = this.findQuadrantByProductPriceType(quadrants, point);
    const quadrantByCode = this.findQuadrantByCode(quadrants, point?.quadrant);
    const quadrantByCoordinates = this.findQuadrantByCoordinates(quadrants, x, y);
    const quadrantByIndex = this.findQuadrantByIndex(quadrants, point?.quadrantIndex);
    const selectedProductPriceType = this.getSelectedProductPriceType();

    if (focusedQuadrant) {
      const focusedBaseColor = String(focusedQuadrant?.color || '').trim();
      if (focusedBaseColor) {
        const focusedDarkenAmount = this.resolveNumber(this.data?.bubbleColorDarken, 0.18);
        return this.darkenHexColor(focusedBaseColor, focusedDarkenAmount) || focusedBaseColor;
      }
    }

    if (forceSelectedProductPriceType && selectedProductPriceType && quadrantByProductPriceType) {
      const forcedBaseColor = String(quadrantByProductPriceType?.color || '').trim();
      if (forcedBaseColor) {
        const forcedDarkenAmount = this.resolveNumber(this.data?.bubbleColorDarken, 0.18);
        return this.darkenHexColor(forcedBaseColor, forcedDarkenAmount) || forcedBaseColor;
      }
    }

    if (preferProductPriceType && quadrantByProductPriceType) {
      const productPriceTypeBaseColor = String(quadrantByProductPriceType?.color || '').trim();
      if (productPriceTypeBaseColor) {
        const productPriceTypeDarkenAmount = this.resolveNumber(this.data?.bubbleColorDarken, 0.18);
        return this.darkenHexColor(productPriceTypeBaseColor, productPriceTypeDarkenAmount) || productPriceTypeBaseColor;
      }
    }

    if (!preferProductPriceType && this.isPointOnQuadrantBoundary(quadrants, x, y) && quadrantByProductPriceType) {
      const boundaryBaseColor = String(quadrantByProductPriceType?.color || '').trim();
      if (boundaryBaseColor) {
        const boundaryDarkenAmount = this.resolveNumber(this.data?.bubbleColorDarken, 0.18);
        return this.darkenHexColor(boundaryBaseColor, boundaryDarkenAmount) || boundaryBaseColor;
      }
    }

    let quadrant = preferProductPriceType
      ? (
        this.areSameQuadrants(quadrantByProductPriceType, quadrantByCoordinates)
          ? quadrantByProductPriceType
          : null
      )
        || quadrantByCode
        || quadrantByCoordinates
        || quadrantByIndex
      : quadrantByCode
        || quadrantByProductPriceType
        || quadrantByCoordinates
        || quadrantByIndex;

    if (
      preferProductPriceType
      && this.isOthersQuadrant(quadrantByProductPriceType)
      && !this.isQ1Quadrant(quadrant)
      && (this.isQ2Quadrant(quadrant) || this.isQ4Quadrant(quadrant))
    ) {
      quadrant = quadrantByProductPriceType;
    }

    const baseColor = String(quadrant?.color || '').trim();

    if (!baseColor) {
      return null;
    }

    const darkenAmount = this.resolveNumber(this.data?.bubbleColorDarken, 0.18);
    return this.darkenHexColor(baseColor, darkenAmount) || baseColor;
  }

  private getFocusedQuadrantColorSource(quadrants: any[]): any | null {
    if (this.data?.showSelectedQuadrantOnly !== true || !Array.isArray(quadrants) || !quadrants.length) {
      return null;
    }

    const filters = this.customGlobalFilterService.getCurrentFilters?.() || {};
    const selectedQuadrant = String(
      filters?.requestParams?.selected_quadrant
      ?? filters?.queryParams?.selected_quadrant
      ?? this.data?.requestParams?.selected_quadrant
      ?? this.data?.queryParams?.selected_quadrant
      ?? '',
    ).trim().toUpperCase();
    const selectedQuadrantIndex = Number(
      filters?.requestParams?.selected_quadrant_index
      ?? filters?.queryParams?.selected_quadrant_index
      ?? this.data?.requestParams?.selected_quadrant_index
      ?? this.data?.queryParams?.selected_quadrant_index,
    );

    if (selectedQuadrant) {
      const byCode = this.findQuadrantByCode(quadrants, selectedQuadrant);
      if (byCode) {
        return byCode;
      }
    }

    return Number.isFinite(selectedQuadrantIndex)
      ? this.findQuadrantByIndex(quadrants, selectedQuadrantIndex)
      : null;
  }

  private getSelectedProductPriceType(): string {
    const filters = this.customGlobalFilterService.getCurrentFilters?.() || {};
    return this.normalizeQuadrantColorValue(
      filters?.requestParams?.product_price_type
      ?? filters?.queryParams?.product_price_type
      ?? this.data?.requestParams?.product_price_type
      ?? this.data?.queryParams?.product_price_type,
    );
  }

  private areSameQuadrants(left: any, right: any): boolean {
    if (!left || !right) {
      return false;
    }

    const leftTitle = String(left?.title || '').trim().toUpperCase();
    const rightTitle = String(right?.title || '').trim().toUpperCase();
    if (leftTitle && rightTitle) {
      return leftTitle === rightTitle;
    }

    return Number(left?.xAxis) === Number(right?.xAxis)
      && Number(left?.x2) === Number(right?.x2)
      && Number(left?.yAxis) === Number(right?.yAxis)
      && Number(left?.y2) === Number(right?.y2);
  }

  private isOthersQuadrant(quadrant: any): boolean {
    return this.hasQuadrantTitle(quadrant, 'Q3');
  }

  private isQ1Quadrant(quadrant: any): boolean {
    return this.hasQuadrantTitle(quadrant, 'Q1');
  }

  private isQ2Quadrant(quadrant: any): boolean {
    return this.hasQuadrantTitle(quadrant, 'Q2');
  }

  private isQ4Quadrant(quadrant: any): boolean {
    return this.hasQuadrantTitle(quadrant, 'Q4');
  }

  private hasQuadrantTitle(quadrant: any, quadrantCode: string): boolean {
    return String(quadrant?.title || '').trim().toUpperCase().includes(quadrantCode);
  }

  private findQuadrantByCode(quadrants: any[], quadrantCode: any): any | null {
    const normalizedQuadrantCode = String(quadrantCode ?? '').trim().toUpperCase();
    if (!Array.isArray(quadrants) || !quadrants.length || !normalizedQuadrantCode) {
      return null;
    }

    return quadrants.find((quadrant: any) =>
      String(quadrant?.title || '').trim().toUpperCase().includes(normalizedQuadrantCode),
    ) || null;
  }

  private findQuadrantByProductPriceType(quadrants: any[], point: any): any | null {
    if (!Array.isArray(quadrants) || !quadrants.length) {
      return null;
    }

    const filters = this.customGlobalFilterService.getCurrentFilters?.() || {};
    const productPriceType = this.normalizeQuadrantColorValue(
      point?.product_price_type
        ?? point?.productPriceType
        ?? point?.quarter
        ?? filters?.requestParams?.product_price_type
        ?? filters?.queryParams?.product_price_type
        ?? this.data?.requestParams?.product_price_type
        ?? this.data?.queryParams?.product_price_type,
    );

    if (!productPriceType) {
      return null;
    }

    const quadrantValueMap = this.data?.quadrantValueMap || {};
    const matchingEntry = Object.entries(quadrantValueMap).find(([, value]) =>
      this.normalizeQuadrantColorValue(value) === productPriceType,
    );

    if (matchingEntry) {
      const quadrantCode = String(matchingEntry[0]);
      // Match the title ("Q1-Star") first; list position is only a fallback, since
      // quadrantBackgrounds need not be ordered Q1..Q4 (index 0 can be "Q3-Others").
      const byTitle = quadrants.find((quadrant: any) =>
        String(quadrant?.title || '').toLowerCase().includes(quadrantCode.toLowerCase()),
      );
      if (byTitle) {
        return byTitle;
      }

      const expectedIndex = Number(quadrantCode.replace(/[^\d]/g, '')) - 1;
      const byIndex = Number.isFinite(expectedIndex) ? quadrants[expectedIndex] : null;
      if (byIndex) {
        return byIndex;
      }
    }

    return quadrants.find((quadrant: any) =>
      this.normalizeQuadrantColorValue(quadrant?.title).includes(productPriceType),
    ) || null;
  }

  private normalizeQuadrantColorValue(value: any): string {
    const normalized = String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');

    if (normalized.includes('review') || normalized.includes('other')) {
      return 'others';
    }

    return normalized;
  }

  private findQuadrantByCoordinates(quadrants: any[], x: number, y: number): any | null {
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }

    const matches = quadrants
      .map((quadrant: any) => {
      const minX = Math.min(Number(quadrant?.xAxis), Number(quadrant?.x2));
      const maxX = Math.max(Number(quadrant?.xAxis), Number(quadrant?.x2));
      const minY = Math.min(Number(quadrant?.yAxis), Number(quadrant?.y2));
      const maxY = Math.max(Number(quadrant?.yAxis), Number(quadrant?.y2));

      const isMatch = Number.isFinite(minX)
        && Number.isFinite(maxX)
        && Number.isFinite(minY)
        && Number.isFinite(maxY)
        && x >= minX
        && x <= maxX
        && y >= minY
        && y <= maxY;

      return isMatch ? { quadrant, minX, minY } : null;
    })
      .filter(Boolean) as Array<{ quadrant: any; minX: number; minY: number }>;

    if (!matches.length) {
      return null;
    }

    const tolerance = 0.000001;
    const preferred = matches.find((match) =>
      Math.abs(x - match.minX) <= tolerance || Math.abs(y - match.minY) <= tolerance,
    );

    return (preferred || matches[0]).quadrant;
  }

  private findQuadrantByIndex(quadrants: any[], quadrantIndex: any): any | null {
    const index = Number(quadrantIndex);
    return Number.isFinite(index) ? quadrants[index] || null : null;
  }

  private isPointOnQuadrantBoundary(quadrants: any[], x: number, y: number): boolean {
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Array.isArray(quadrants) || quadrants.length < 2) {
      return false;
    }

    const tolerance = 0.000001;
    return quadrants.some((quadrant: any) => {
      const minX = Math.min(Number(quadrant?.xAxis), Number(quadrant?.x2));
      const maxX = Math.max(Number(quadrant?.xAxis), Number(quadrant?.x2));
      const minY = Math.min(Number(quadrant?.yAxis), Number(quadrant?.y2));
      const maxY = Math.max(Number(quadrant?.yAxis), Number(quadrant?.y2));

      if (!Number.isFinite(minX) || !Number.isFinite(maxX) || !Number.isFinite(minY) || !Number.isFinite(maxY)) {
        return false;
      }

      const onVerticalEdge = (Math.abs(x - minX) <= tolerance || Math.abs(x - maxX) <= tolerance)
        && y >= minY - tolerance
        && y <= maxY + tolerance;
      const onHorizontalEdge = (Math.abs(y - minY) <= tolerance || Math.abs(y - maxY) <= tolerance)
        && x >= minX - tolerance
        && x <= maxX + tolerance;

      return onVerticalEdge || onHorizontalEdge;
    });
  }

  private darkenHexColor(color: string, amount: number): string | null {
    const normalized = color.trim().replace(/^#/, '');
    const hex = normalized.length === 3
      ? normalized.split('').map((char) => `${char}${char}`).join('')
      : normalized;

    if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
      return null;
    }

    const factor = Math.max(0, Math.min(1, 1 - amount));
    const red = Math.round(parseInt(hex.slice(0, 2), 16) * factor);
    const green = Math.round(parseInt(hex.slice(2, 4), 16) * factor);
    const blue = Math.round(parseInt(hex.slice(4, 6), 16) * factor);

    return `#${[red, green, blue]
      .map((value) => value.toString(16).padStart(2, '0'))
      .join('')}`;
  }

  protected override resolveVisibleBubbleSize(rawBubbleSize: any, fallbackSize: any): any {
    // TODO: Add your custom logic here
    return super.resolveVisibleBubbleSize(rawBubbleSize, fallbackSize);
  }

  protected override normalizeQuadrantPoint(point: any, quadrantIndex: any, index: any): any {
    let normalized = super.normalizeQuadrantPoint(point, quadrantIndex, index);
    normalized = this.alignPointToConfiguredQuadrant(normalized);

    // The API buckets each point into q1/q2/q3/q4 based on its own
    // classification, which doesn't always agree with the point's plotted
    // x/y (e.g. a "Key Accounts" customer can still have a competitiveness
    // score > 5). That mismatch is what makes bubbles render straddling or
    // outside their quadrant's rectangle. Unlike alignPointToConfiguredQuadrant
    // (which re-derives the target quadrant from product_price_type, falling
    // back to the *globally selected* filter when a point has none — risky,
    // since it can silently reassign every point to one quadrant), this uses
    // the quadrantIndex the caller already passed in for THIS point, which is
    // always correct and point-specific.
    if (this.data?.clampBubblesToQuadrantBounds === true) {
      normalized = this.clampPointToQuadrantIndex(normalized, quadrantIndex);
    }

    if (this.data?.bubbleColorMode === 'quadrant') {
      const quadrantColor = this.resolveQuadrantBubbleColor(normalized);
      if (quadrantColor) {
        normalized = {
          ...normalized,
          color: quadrantColor,
        };
      }
    }

    const labelOverrides: Record<string, any> = {};

    if (this.shouldMoveBubbleLabelBelowQuadrantTitle(normalized, quadrantIndex)) {
      labelOverrides['position'] = 'bottom';
      labelOverrides['distance'] = this.resolveNumber(this.data?.bubbleLabelCollisionDistance, 4);
    }

    const preferredLongLabelSide = this.resolvePreferredLongBubbleLabelSide(normalized);
    if (preferredLongLabelSide === 'right') {
      labelOverrides['position'] = 'right';
      labelOverrides['align'] = 'left';
      labelOverrides['verticalAlign'] = 'middle';
      labelOverrides['distance'] = this.resolveNumber(this.data?.bubbleLabelEdgeDistance, 6);
    } else if (preferredLongLabelSide === 'left') {
      labelOverrides['position'] = 'left';
      labelOverrides['align'] = 'right';
      labelOverrides['verticalAlign'] = 'middle';
      labelOverrides['distance'] = this.resolveNumber(this.data?.bubbleLabelEdgeDistance, 6);
    }

    if (this.shouldMoveBubbleLabelInsideFromRightEdge(normalized, quadrantIndex)) {
      labelOverrides['position'] = 'left';
      labelOverrides['align'] = 'right';
      labelOverrides['verticalAlign'] = 'middle';
      labelOverrides['distance'] = this.resolveNumber(this.data?.bubbleLabelEdgeDistance, 6);
    }

    Object.assign(labelOverrides, this.resolveConfiguredBubbleLabelOverrides(normalized));

    if (!Object.keys(labelOverrides).length) {
      return normalized;
    }

    return {
      ...normalized,
      label: {
        ...(normalized?.label && typeof normalized.label === 'object' ? normalized.label : {}),
        ...labelOverrides,
      },
    };
  }

  private resolveConfiguredBubbleLabelOverrides(point: any): Record<string, any> {
    const overrideMap = this.data?.bubbleLabelOverridesByCustomer;
    if (!overrideMap || typeof overrideMap !== 'object' || Array.isArray(overrideMap)) {
      return {};
    }

    const customerName = String(
      point?.customer_name
      ?? point?.label
      ?? point?.name
      ?? '',
    ).trim();

    if (!customerName) {
      return {};
    }

    const directOverride = overrideMap[customerName];
    if (!directOverride || typeof directOverride !== 'object' || Array.isArray(directOverride)) {
      return {};
    }

    return { ...directOverride };
  }

  private alignPointToConfiguredQuadrant(point: any): any {
    if (this.data?.forceQuadrantPlacementByProductPriceType !== true) {
      return point;
    }

    const quadrants = Array.isArray(this.data?.quadrantBackgrounds) ? this.data.quadrantBackgrounds : [];
    if (!quadrants.length) {
      return point;
    }

    const targetQuadrant = this.findQuadrantByProductPriceType(quadrants, point);
    if (!targetQuadrant) {
      return point;
    }

    const x = Number(point?.x ?? point?.value?.[0]);
    const y = Number(point?.y ?? point?.value?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return point;
    }

    const epsilon = this.resolveNumber(this.data?.quadrantPlacementEpsilon, 0.02);
    const bounds = this.toQuadrantBounds(targetQuadrant);
    if (!bounds) {
      return point;
    }

    const nextX = Math.max(bounds.minX + epsilon, Math.min(bounds.maxX - epsilon, x));
    const nextY = Math.max(bounds.minY + epsilon, Math.min(bounds.maxY - epsilon, y));
    const quadrantIndex = this.findQuadrantIndex(quadrants, targetQuadrant);
    const quadrantCode = quadrantIndex >= 0 ? `Q${quadrantIndex + 1}` : point?.quadrant;

    return {
      ...point,
      x: nextX,
      y: nextY,
      quadrant: quadrantCode,
      quadrantIndex: quadrantIndex >= 0 ? quadrantIndex : point?.quadrantIndex,
      value: [nextX, nextY, quadrantIndex >= 0 ? quadrantIndex : point?.quadrantIndex, point?.size],
    };
  }

  private clampPointToQuadrantIndex(point: any, quadrantIndex: any): any {
    const idx = Number(point?.quadrantIndex ?? quadrantIndex);
    if (!Number.isFinite(idx) || idx < 0) {
      return point;
    }

    const bounds = this.getQuadrantBounds(idx);
    if (!bounds) {
      return point;
    }

    const x = Number(point?.x ?? point?.value?.[0]);
    const y = Number(point?.y ?? point?.value?.[1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return point;
    }

    const epsilon = this.resolveNumber(this.data?.quadrantPlacementEpsilon, 0.02);
    const nextX = Math.max(bounds.minX + epsilon, Math.min(bounds.maxX - epsilon, x));
    const nextY = Math.max(bounds.minY + epsilon, Math.min(bounds.maxY - epsilon, y));

    if (nextX === x && nextY === y) {
      return point;
    }

    return {
      ...point,
      x: nextX,
      y: nextY,
      value: [nextX, nextY, point?.quadrantIndex ?? idx, point?.size],
    };
  }

  private toQuadrantBounds(quadrant: any): { minX: number; maxX: number; minY: number; maxY: number } | null {
    const minX = Math.min(Number(quadrant?.xAxis), Number(quadrant?.x2));
    const maxX = Math.max(Number(quadrant?.xAxis), Number(quadrant?.x2));
    const minY = Math.min(Number(quadrant?.yAxis), Number(quadrant?.y2));
    const maxY = Math.max(Number(quadrant?.yAxis), Number(quadrant?.y2));
    return [minX, maxX, minY, maxY].every((value) => Number.isFinite(value))
      ? { minX, maxX, minY, maxY }
      : null;
  }

  private findQuadrantIndex(quadrants: any[], targetQuadrant: any): number {
    return quadrants.findIndex((quadrant: any) => this.areSameQuadrants(quadrant, targetQuadrant));
  }

  private shouldMoveBubbleLabelBelowQuadrantTitle(point: any, quadrantIndex: any): boolean {
    if (this.data?.avoidQuadrantTitleLabelOverlap !== true || this.data?.showBubbleLabels !== true) {
      return false;
    }

    const bounds = this.getQuadrantBounds(Number(quadrantIndex));
    if (!bounds) {
      return false;
    }

    const y = Number(point?.y ?? point?.value?.[1]);
    if (!Number.isFinite(y)) {
      return false;
    }

    const height = Math.abs(bounds.maxY - bounds.minY);
    if (!Number.isFinite(height) || height <= 0) {
      return false;
    }

    const topBandRatio = this.resolveNumber(this.data?.bubbleLabelTitleOverlapTopBandRatio, 0.25);
    const boundedRatio = Math.max(0, Math.min(1, topBandRatio));
    return y >= bounds.maxY - height * boundedRatio;
  }

  private shouldMoveBubbleLabelInsideFromRightEdge(point: any, quadrantIndex: any): boolean {
    if (this.data?.showBubbleLabels !== true) {
      return false;
    }

    const bounds = this.getQuadrantBounds(Number(quadrantIndex));
    if (!bounds) {
      return false;
    }

    const x = Number(point?.x ?? point?.value?.[0]);
    if (!Number.isFinite(x)) {
      return false;
    }

    const width = Math.abs(bounds.maxX - bounds.minX);
    if (!Number.isFinite(width) || width <= 0) {
      return false;
    }

    const labelText = String(
      point?.customer_name
      ?? point?.label
      ?? point?.name
      ?? '',
    ).trim();
    if (!labelText) {
      return false;
    }

    const edgeRatio = this.resolveNumber(this.data?.bubbleLabelRightEdgeThresholdRatio, 0.18);
    const boundedEdgeRatio = Math.max(0, Math.min(0.45, edgeRatio));
    const longTextLength = Math.max(8, Math.round(this.resolveNumber(this.data?.bubbleLabelLongTextLength, 16)));

    return labelText.length >= longTextLength && x >= bounds.maxX - width * boundedEdgeRatio;
  }

  private resolvePreferredLongBubbleLabelSide(point: any): 'left' | 'right' | null {
    if (this.data?.showBubbleLabels !== true) {
      return null;
    }

    const labelPosition = String(this.data?.bubbleLabelPosition || 'top').trim().toLowerCase();
    if (labelPosition !== 'top') {
      return null;
    }

    const x = Number(point?.x ?? point?.value?.[0]);
    if (!Number.isFinite(x)) {
      return null;
    }

    const labelText = String(
      point?.customer_name
      ?? point?.label
      ?? point?.name
      ?? '',
    ).trim();
    if (!labelText) {
      return null;
    }

    const longTextLength = Math.max(8, Math.round(this.resolveNumber(this.data?.bubbleLabelLongTextLength, 16)));
    if (labelText.length < longTextLength) {
      return null;
    }

    const chartMinX = this.resolveNumber(this.data?.xAxis?.min, 0);
    const chartMaxX = this.resolveNumber(this.data?.xAxis?.max, 10);
    const chartMidX = (chartMinX + chartMaxX) / 2;

    return x <= chartMidX ? 'right' : 'left';
  }

  protected override getQuadrantAreas(focusedQuadrantIndex: any): any {
    const focusedQuadrantCode =
      focusedQuadrantIndex === null || focusedQuadrantIndex < 0
        ? null
        : `Q${focusedQuadrantIndex + 1}`;

    return (this.data?.quadrantBackgrounds || [])
      .filter((quad: any) =>
        focusedQuadrantCode === null || this.extractQuadrantCodeFromTitle(quad?.title) === focusedQuadrantCode,
      )
      .map((quad: any) => {
        const quadrantCode = this.extractQuadrantCodeFromTitle(quad?.title);
        const labelPosition = quadrantCode === 'Q1'
          ? 'insideTopRight'
          : quadrantCode === 'Q2'
            ? 'insideTopRight'
            : quadrantCode === 'Q3'
              ? 'insideTopLeft'
              : 'insideTopLeft';

        return ([
          {
            name: '',
            xAxis: quad.xAxis,
            yAxis: quad.yAxis,
            itemStyle: { color: quad.color },
            label: {
              show: true,
              formatter: quad.title,
              position: labelPosition,
              distance: 10,
              color: '#111827',
              fontSize: 12,
              fontWeight: 'bold',
            },
          },
          {
            xAxis: quad.x2,
            yAxis: quad.y2,
          },
        ]);
      });
  }

  protected override shouldZoomToFocusedQuadrant(): any {
    // TODO: Add your custom logic here
    return super.shouldZoomToFocusedQuadrant();
  }

  protected override getQuadrantBounds(quadrantIndex: any): any {
    // TODO: Add your custom logic here
    return super.getQuadrantBounds(quadrantIndex);
  }

  protected override resolveFocusedQuadrantSelection(): any {
    // TODO: Add your custom logic here
    return super.resolveFocusedQuadrantSelection();
  }

  protected override normalizeCategoryValue(value: any): any {
    // TODO: Add your custom logic here
    return super.normalizeCategoryValue(value);
  }

  protected override extractQuadrantCodeFromTitle(title: any): any {
    // TODO: Add your custom logic here
    return super.extractQuadrantCodeFromTitle(title);
  }

  protected override resolveChartData(source: any): any {
    this.indexApplicationRows(source);
    return super.resolveChartData(source);
  }

  protected override resolvePath(obj: any, path: any): any {
    // TODO: Add your custom logic here
    return super.resolvePath(obj, path);
  }

  protected override buildTooltipContext(params: any): any {
    const context = super.buildTooltipContext(params);
    const mappedFilterValue = String(
      context?.product_price_type ??
      this.resolveQuadrantFilterValue(
        String(context?.quadrant || ''),
        Number(context?.quadrantIndex ?? context?.value?.[2] ?? -1),
      ),
    );
    const application = this.resolveApplicationValue(context, mappedFilterValue);

    if (application === undefined || application === null || application === '') {
      return context;
    }

    return {
      ...context,
      application,
    };
  }

  protected override interpolateTooltipTemplate(template: any, data: any): any {
    // TODO: Add your custom logic here
    return super.interpolateTooltipTemplate(template, data);
  }

  protected override resolveTooltipValue(data: any, key: any): any {
    // TODO: Add your custom logic here
    return super.resolveTooltipValue(data, key);
  }

  protected override normalizeKey(value: any): any {
    // TODO: Add your custom logic here
    return super.normalizeKey(value);
  }

  private resolveStableColor(value: string): string {
    const normalized = this.normalizeColorSeed(value);
    const existingColor = this.bubbleColorBySeed.get(normalized);
    if (existingColor) {
      return existingColor;
    }

    const palette = Array.isArray(this.data?.bubbleColorPalette) && this.data.bubbleColorPalette.length
      ? this.data.bubbleColorPalette
      : ['#336987', '#e65349', '#4f7fe8', '#36a98a', '#e9922b', '#7c4dff', '#00a6b4', '#c44e7d', '#64748b', '#14b8a6'];
    const color = String(palette[this.bubbleColorBySeed.size % palette.length]);

    this.bubbleColorBySeed.set(normalized, color);
    return color;
  }

  private normalizeColorSeed(value: string): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  protected override findValueByNormalizedKey(source: any, normalizedKey: any): any {
    // TODO: Add your custom logic here
    return super.findValueByNormalizedKey(source, normalizedKey);
  }

  protected override setupBubbleClickEvent(): any {
    // TODO: Add your custom logic here
    return super.setupBubbleClickEvent();
  }

  protected override setupQuadrantAreaClickEvent(): any {
    // TODO: Add your custom logic here
    return super.setupQuadrantAreaClickEvent();
  }

  protected override shouldEnableQuadrantAreaClick(): any {
    // TODO: Add your custom logic here
    return super.shouldEnableQuadrantAreaClick();
  }

  protected override handleBubblePointInteraction(point: any, params?: any): any {
    if (!point) return;
    const now = Date.now();
    if (now - this.customLastBubbleInteractionAt < 250) {
      return;
    }
    this.customLastBubbleInteractionAt = now;

    const raw = Array.isArray(point?.value) ? point.value : point?.value ?? point;
    const quadrantIndex = Number(point?.quadrantIndex ?? raw?.[2]);
    const quadrant = String(point?.quadrant ?? (Number.isFinite(quadrantIndex) ? `Q${quadrantIndex + 1}` : ''));
    const filterKey = String(this.data?.filterKey || 'quadrant').trim();
    if (!filterKey || !quadrant) return;

    const mappedFilterValue = String(
      point?.product_price_type ??
      this.resolveQuadrantFilterValue(quadrant, quadrantIndex),
    );
    const application = this.resolveApplicationValue(point, mappedFilterValue);

    const payload: Record<string, any> = {
      [filterKey]: mappedFilterValue,
      product_price_type: mappedFilterValue,
      customer_name: point?.customer_name,
      engine_model: point?.engine_model,
      certification: point?.certification,
      key: point?.key ?? point?.name,
      label: point?.label ?? point?.name,
      x: point?.x ?? raw?.[0],
      y: point?.y ?? raw?.[1],
      quadrant,
      quadrantIndex,
    };

    if (application !== undefined && application !== null && application !== '') {
      payload['application'] = application;
    }

    const eventContext = {
      ...point,
      ...payload,
      cluster: quadrantIndex,
    };

    this.customChartFilterHandler.updateFilterSelection(this.data, payload, eventContext);

    if (!this.data?.navigateAction) {
      return;
    }

    const filters = this.customGlobalFilterService.getCurrentFilters();
    executeNavigationFromConfig(this.customRouter, this.data, {
      ...(params || {}),
      componentType: params?.componentType || 'series',
      data: point,
      name: point?.name ?? point?.label,
      value: point?.value,
      seriesName: point?.legend,
    }, {
      filters,
      requestParams: {
        ...(filters?.requestParams || {}),
        ...payload,
        uid: point?.uid ?? null,
        price_increase: point?.price_increase ?? null,
        selected_quadrant: quadrant,
        selected_quadrant_index: quadrantIndex,
      },
      row: eventContext,
      point: eventContext,
      event: eventContext,
    });
  }

  private resolveApplicationValue(point: any, productPriceType: any): any {
    const directValue = this.pickApplicationValue(point);
    if (directValue !== undefined && directValue !== null && directValue !== '') {
      return directValue;
    }

    return this.lookupApplicationValue(point, productPriceType);
  }

  private indexApplicationRows(source: any): void {
    this.applicationByRowKey.clear();

    const visit = (value: any): void => {
      if (Array.isArray(value)) {
        value.forEach(visit);
        return;
      }

      if (!value || typeof value !== 'object') {
        return;
      }

      const application = this.pickApplicationValue(value);
      if (application !== undefined && application !== null && application !== '') {
        this.buildApplicationLookupKeys(value).forEach((key) => {
          this.applicationByRowKey.set(key, application);
        });
      }

      Object.keys(value).forEach((key) => visit(value[key]));
    };

    visit(source);
  }

  private lookupApplicationValue(point: any, productPriceType: any): any {
    const filters = this.customGlobalFilterService.getCurrentFilters();
    const requestParams = filters?.requestParams || {};
    const candidate = {
      ...requestParams,
      ...point,
      customer_name: point?.customer_name ?? requestParams.customer_name,
      product_price_type: point?.product_price_type ?? productPriceType ?? requestParams.product_price_type,
    };

    for (const key of this.buildApplicationLookupKeys(candidate)) {
      if (this.applicationByRowKey.has(key)) {
        return this.applicationByRowKey.get(key);
      }
    }

    return undefined;
  }

  private buildApplicationLookupKeys(source: any): string[] {
    const customer = this.normalizeLookupValue(source?.customer_name);
    const engine = this.normalizeLookupValue(source?.engine_model);
    const certification = this.normalizeLookupValue(source?.certification);
    const productType = this.normalizeLookupValue(source?.product_price_type);
    const x = this.normalizeLookupNumber(source?.x ?? source?.x_axis ?? source?.value?.[0]);
    const y = this.normalizeLookupNumber(source?.y ?? source?.y_axis ?? source?.value?.[1]);
    const keys: string[] = [];

    if (customer && engine && certification && productType && x && y) {
      keys.push([customer, engine, certification, productType, x, y].join('|'));
    }

    if (engine && certification && productType && x && y) {
      keys.push([engine, certification, productType, x, y].join('|'));
    }

    if (customer && engine && certification && productType) {
      keys.push([customer, engine, certification, productType].join('|'));
    }

    if (engine && certification && productType) {
      keys.push([engine, certification, productType].join('|'));
    }

    if (customer && engine && certification) {
      keys.push([customer, engine, certification].join('|'));
    }

    if (engine && certification) {
      keys.push([engine, certification].join('|'));
    }

    return keys;
  }

  private pickApplicationValue(source: any): any {
    return (
      source?.application ??
      source?.segment ??
      source?.Segment ??
      source?.SEGMENT ??
      source?.ebu_segment ??
      source?.EBU_segment ??
      source?.EBU_SEGMENT
    );
  }

  private normalizeLookupValue(value: any): string {
    return String(Array.isArray(value) ? value[0] : value ?? '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private normalizeLookupNumber(value: any): string {
    const numericValue = Number(Array.isArray(value) ? value[0] : value);
    return Number.isFinite(numericValue) ? numericValue.toFixed(6) : '';
  }

  protected override resolveBubblePointFromPixel(pixel: any): any {
    // TODO: Add your custom logic here
    return super.resolveBubblePointFromPixel(pixel);
  }

  protected override resolveQuadrantFilterValue(quadrant: any, quadrantIndex: any): any {
    // TODO: Add your custom logic here
    return super.resolveQuadrantFilterValue(quadrant, quadrantIndex);
  }
}
